import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { Button } from '@/shared/ui';
import type { CalendarAxis } from '../model/calendarLayout';

/**
 * Slepená skupina tlačítek (přepínač) — tlačítka sdílí jednu vlasovou linku, takže mezi nimi
 * nevzniká dvojitý rámeček. Sdílený `SegmentedButtons` má jiný vzhled (plné primary tlačítko);
 * kalendářový toolbar drží tlumený přepínač z návrhu.
 */
function ToolbarGroup({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span
      role="group"
      className={cn(
        'inline-flex [&>button]:rounded-none [&>button:not(:first-child)]:-ml-px',
        '[&>button:first-child]:rounded-l-phx [&>button:last-child]:rounded-r-phx',
        '[&>button[data-active=true]]:relative [&>button[data-active=true]]:z-[1]',
        className
      )}
    >
      {children}
    </span>
  );
}

interface CalendarToolbarProps {
  axis: CalendarAxis;
  onAxisChange: (axis: CalendarAxis) => void;
  /** „Reálná doprava“ = spoje sedí na dnech, kdy skutečně jedou. */
  realTime: boolean;
  onRealTimeChange: (realTime: boolean) => void;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onToday: () => void;
}

/**
 * Horní lišta kalendáře: posun po měsících, „Dnes“, přepínač zobrazení časů dopravy
 * (logická / reálná) a přepínač osy (týdny / souvislá osa).
 *
 * Který měsíc je zrovna pod výřezem, se odsud nečte — v týdnech to říká sticky popisek nad prvním
 * týdnem měsíce a na ose pruh nad dny. Filtr typu dopravy tu taky není: patří do lišty filtrů nad
 * stromem, aby byl jeden zdroj pravdy pro strom i kalendář.
 */
export function CalendarToolbar({
  axis,
  onAxisChange,
  realTime,
  onRealTimeChange,
  onPrevMonth,
  onNextMonth,
  onToday,
}: CalendarToolbarProps) {
  const { t } = useTranslation('venua-planner');

  return (
    <div className="bg-phx-surface-2 border-phx-border gap-phx-3 px-phx-5 py-phx-3_5 flex min-h-[30px] shrink-0 flex-wrap items-center border-b">
      <ToolbarGroup>
        <Button
          size="icon"
          onClick={onPrevMonth}
          aria-label={t('calendar.toolbar.prevMonth')}
          data-testid="calendar-prev-month"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </Button>
        <Button
          size="icon"
          onClick={onNextMonth}
          aria-label={t('calendar.toolbar.nextMonth')}
          data-testid="calendar-next-month"
        >
          <ChevronRight className="h-3.5 w-3.5" />
        </Button>
      </ToolbarGroup>

      <Button onClick={onToday} data-testid="calendar-today">
        {t('calendar.toolbar.today')}
      </Button>

      <ToolbarGroup className="ml-phx-2">
        <Button
          active={!realTime}
          data-active={!realTime}
          title={t('calendar.toolbar.logicalTimeHint')}
          onClick={() => onRealTimeChange(false)}
          data-testid="calendar-time-logical"
        >
          {t('calendar.toolbar.logicalTime')}
        </Button>
        <Button
          active={realTime}
          data-active={realTime}
          title={t('calendar.toolbar.realTimeHint')}
          onClick={() => onRealTimeChange(true)}
          data-testid="calendar-time-real"
        >
          {t('calendar.toolbar.realTime')}
        </Button>
      </ToolbarGroup>

      <ToolbarGroup className="ml-phx-2">
        <Button
          active={axis === 'weeks'}
          data-active={axis === 'weeks'}
          title={t('calendar.toolbar.weeksHint')}
          onClick={() => onAxisChange('weeks')}
          data-testid="calendar-axis-weeks"
        >
          {t('calendar.toolbar.weeks')}
        </Button>
        <Button
          active={axis === 'timeline'}
          data-active={axis === 'timeline'}
          title={t('calendar.toolbar.timelineHint')}
          onClick={() => onAxisChange('timeline')}
          data-testid="calendar-axis-timeline"
        >
          {t('calendar.toolbar.timeline')}
        </Button>
      </ToolbarGroup>
    </div>
  );
}
